import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  GraduationCap, BookOpen, Award, Clock, ExternalLink, 
  AlertTriangle, CheckCircle2, PlayCircle, RefreshCw,
  Target, TrendingUp, Lightbulb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Training() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [completions, setCompletions] = useState([]);
  
  const fetchData = async () => {
    try {
      const [resourcesRes, recsRes, assignRes, compRes] = await Promise.all([
        fetch(`${API_URL}/api/training/resources`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/training/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/training/assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/training/completions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (resourcesRes.ok) setResources(await resourcesRes.json());
      if (recsRes.ok) {
        const data = await recsRes.json();
        setRecommendations(data.recommendations || []);
      }
      if (assignRes.ok) setAssignments(await assignRes.json());
      if (compRes.ok) setCompletions(await compRes.json());
    } catch (error) {
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const markComplete = async (resourceId) => {
    try {
      const response = await fetch(`${API_URL}/api/training/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_id: resourceId,
          completion_date: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('Training marked as complete');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  
  const getTypeIcon = (type) => {
    switch (type) {
      case 'online_course': return <BookOpen className="h-4 w-4" />;
      case 'webinar': return <PlayCircle className="h-4 w-4" />;
      case 'certification': return <Award className="h-4 w-4" />;
      default: return <GraduationCap className="h-4 w-4" />;
    }
  };
  
  const completedIds = new Set(completions.map(c => c.resource_id));
  const completionRate = resources.length > 0 
    ? Math.round((completions.length / resources.length) * 100) 
    : 0;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-teal-500" />
            Training Center
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Improve skills based on quality metrics and recommendations
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <BookOpen className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Available</p>
                <p className="text-xl font-bold">{resources.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-xl font-bold">{completions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Target className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Recommended</p>
                <p className="text-xl font-bold">{recommendations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Progress</p>
                <p className="text-xl font-bold">{completionRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Progress Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Training Completion</span>
            <span className="text-sm text-muted-foreground">{completions.length} of {resources.length} courses</span>
          </div>
          <Progress value={completionRate} className="h-2" />
        </CardContent>
      </Card>
      
      <Tabs defaultValue="recommendations" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-3 h-auto">
          <TabsTrigger value="recommendations" className="gap-2">
            <Lightbulb className="h-4 w-4" /> Recommended
            {recommendations.length > 0 && (
              <Badge variant="secondary" className="ml-1">{recommendations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="catalog" className="gap-2">
            <BookOpen className="h-4 w-4" /> All Courses
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <Award className="h-4 w-4" /> Completed
          </TabsTrigger>
        </TabsList>
        
        {/* Recommendations Tab */}
        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                AI-Recommended Training
              </CardTitle>
              <CardDescription>
                Based on your below-average metrics, we recommend these courses
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p className="font-medium">All metrics are above average</p>
                  <p className="text-sm">No urgent training recommendations at this time</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, idx) => (
                    <div key={idx} className="p-4 rounded-lg border bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border-amber-200">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getPriorityColor(rec.priority)}>
                              {rec.priority.toUpperCase()}
                            </Badge>
                            {getTypeIcon(rec.resource.type)}
                            <span className="text-xs text-muted-foreground">{rec.resource.type.replace('_', ' ')}</span>
                          </div>
                          <h4 className="font-semibold">{rec.resource.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{rec.resource.description}</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-2 font-medium">
                            Why: {rec.reason}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {rec.resource.duration}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {completedIds.has(rec.resource_id) ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
                            </Badge>
                          ) : (
                            <>
                              <Button variant="outline" size="sm" asChild>
                                <a href={rec.resource.url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-1" /> Start
                                </a>
                              </Button>
                              <Button size="sm" onClick={() => markComplete(rec.resource_id)}>
                                <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Done
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* All Courses Tab */}
        <TabsContent value="catalog">
          <Card>
            <CardHeader>
              <CardTitle>Training Catalog</CardTitle>
              <CardDescription>All available training resources</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {resources.map((resource) => (
                  <div 
                    key={resource.id} 
                    className={`p-4 rounded-lg border ${
                      completedIds.has(resource.id) 
                        ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200' 
                        : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(resource.type)}
                        <Badge variant="outline" className="text-xs">
                          {resource.type.replace('_', ' ')}
                        </Badge>
                      </div>
                      {completedIds.has(resource.id) && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Done
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-semibold mb-1">{resource.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {resource.duration}
                      </span>
                      {!completedIds.has(resource.id) && (
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" asChild>
                            <a href={resource.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => markComplete(resource.id)}>
                            Complete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Completed Tab */}
        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-emerald-500" />
                Completed Training
              </CardTitle>
              <CardDescription>Your training achievements</CardDescription>
            </CardHeader>
            <CardContent>
              {completions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No completed training yet</p>
                  <p className="text-sm">Start with the recommended courses above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completions.map((completion, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{completion.resource_title}</p>
                        <p className="text-xs text-muted-foreground">
                          Completed on {new Date(completion.completion_date).toLocaleDateString()}
                        </p>
                      </div>
                      {completion.score && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          Score: {completion.score}%
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
